import { useEffect, useState } from "react";
import { X, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { getPassportNotice, setPassportNotice, subscribePassportNotice } from "../lib/passportNotice";

const REASONS = { not_on_menu: "not on this menu", unavailable: "currently unavailable" };

export const PassportNotice = () => {
  const [notice, setNotice] = useState(getPassportNotice());
  const [pickupLoc, setPickupLoc] = useState(null);

  useEffect(() => subscribePassportNotice(setNotice), []);
  useEffect(() => {
    if (notice?.deliverySwitched) {
      api.get("/locations").then(({ data }) => {
        setPickupLoc(data.find((l) => l.status === "active") || null);
      }).catch(() => {});
    }
  }, [notice?.deliverySwitched]);

  if (!notice) return null;
  const bad = notice.type === "failed" || notice.type === "none_resolved";
  const dismiss = () => setPassportNotice(null);

  return (
    <div data-testid="passport-notice"
      className={`mx-auto max-w-5xl mt-4 px-4 sm:px-6`}>
      <div className={`rounded-2xl border-2 p-4 flex gap-3 ${bad ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
        {bad ? <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} aria-hidden="true" />
             : <CheckCircle2 className="text-amber-700 shrink-0 mt-0.5" size={20} aria-hidden="true" />}
        <div className="flex-1 text-sm">
          {notice.type === "failed" && (
            <p className="font-bold text-red-700" data-testid="passport-notice-failed">{notice.detail}</p>
          )}
          {notice.type === "none_resolved" && (
            <p className="font-bold text-red-700" data-testid="passport-notice-none">
              We couldn't add the items from your saved order — please rebuild it from the menu below.
            </p>
          )}
          {(notice.type === "ok" || notice.type === "partial") && (
            <p className="font-bold text-amber-900" data-testid="passport-notice-added">
              {notice.added} item{notice.added === 1 ? "" : "s"} from your Urban Passport order {notice.added === 1 ? "was" : "were"} added to your cart.
            </p>
          )}
          {notice.missing?.length > 0 && (
            <div className="mt-1.5">
              <p className={`font-semibold ${bad ? "text-red-700" : "text-amber-900"}`}>
                {notice.type === "none_resolved" ? "The items in your Passport order:" : "These couldn't be added:"}
              </p>
              <ul className="mt-0.5 space-y-0.5">
                {notice.missing.map((m, i) => (
                  <li key={i} data-testid="passport-notice-missing-item" className={bad ? "text-red-700" : "text-amber-800"}>
                    • {m.name} <span className="opacity-70">({REASONS[m.reason] || m.reason})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {notice.deliverySwitched && (
            <p className="mt-2 font-bold text-amber-900" data-testid="passport-notice-delivery-switched">
              Delivery orders go through Uber Eats, DoorDash or Grubhub, not this checkout — we've switched your order to pickup
              {pickupLoc ? ` at ${pickupLoc.name}. Collect at ${pickupLoc.address}.` : "."}
            </p>
          )}
        </div>
        <button data-testid="passport-notice-dismiss" onClick={dismiss} aria-label="Dismiss"
          className={`shrink-0 self-start ${bad ? "text-red-400 hover:text-red-700" : "text-amber-500 hover:text-amber-800"}`}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
};
